import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert
} from 'reactstrap';
import InputsValidations from '../../InputsValidations.js';

const ContactModal = ({ isOpen, toggle, onSave, contactToEdit, existingContacts = [] }) => {
  const [formData, setFormData] = useState({
    type: 'PHONE',
    value: '',
    isPrimary: true
  });
  const [errors, setErrors] = useState({});
  const [primaryWarning, setPrimaryWarning] = useState('');

  useEffect(() => {
    if (contactToEdit) {
      setFormData({
        type: contactToEdit.type === 'Phone' ? 'PHONE' : 'EMAIL',
        value: contactToEdit.value || '',
        isPrimary: contactToEdit.primary === 'Yes'
      });
    } else {
      setFormData({
        type: 'PHONE',
        value: '',
        isPrimary: true
      });
    }
    setErrors({});
    setPrimaryWarning('');
  }, [contactToEdit, isOpen]);

  const handleInputChange = (field, value) => {
    let processedValue = value;

    // For phone numbers, only allow digits and limit length
    if (field === 'value' && formData.type === 'PHONE') {
      // Remove all non-digit characters and limit to 13 digits
      const digits = value.replace(/\D/g, '').slice(0, 13);
      processedValue = digits;
    }

    // Update form data first
    const newFormData = {
      ...formData,
      [field]: processedValue
    };

    // Check primary contact validation when isPrimary changes OR when type changes
    if ((field === 'isPrimary' && value === true) || (field === 'type' && formData.isPrimary)) {
      const contactType = field === 'type' 
        ? (value === 'PHONE' ? 'Phone' : 'Email')
        : (formData.type === 'PHONE' ? 'Phone' : 'Email');
      
      const hasPrimaryOfType = existingContacts.some(
        c => c.type === contactType && c.primary === 'Yes' && (!contactToEdit || c.id !== contactToEdit.id)
      );
      
      if (hasPrimaryOfType) {
        setPrimaryWarning(`A primary ${contactType} already exists. Another one cannot be assigned.`);
        // If trying to change to a type that already has a primary, uncheck isPrimary
        if (field === 'type') {
          newFormData.isPrimary = false;
        } else if (field === 'isPrimary') {
          // Don't allow checking isPrimary if there's already a primary
          return;
        }
      } else {
        setPrimaryWarning('');
      }
    } else if (field === 'type' || field === 'isPrimary') {
      // Clear warning if changing to a valid state
      setPrimaryWarning('');
    }

    setFormData(newFormData);

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.value || formData.value.trim() === '') {
      newErrors.value = 'Contact value is required';
    } else {
      // Validate email format
      if (formData.type === 'EMAIL') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.value)) {
          newErrors.value = 'Please enter a valid email address';
        }
      }
      
      // Validate phone length (minimum 1, maximum 13 characters)
      if (formData.type === 'PHONE') {
        const digits = formData.value.replace(/\D/g, '');
        if (digits.length < 1) {
          newErrors.value = 'Phone number must have at least 1 digit';
        } else if (digits.length > 13) {
          newErrors.value = 'Phone number cannot exceed 13 digits';
        }
      }
    }

    // Validate primary contact
    if (formData.isPrimary) {
      const contactType = formData.type === 'PHONE' ? 'Phone' : 'Email';
      const hasPrimaryOfType = existingContacts.some(
        c => c.type === contactType && c.primary === 'Yes' && (!contactToEdit || c.id !== contactToEdit.id)
      );
      
      if (hasPrimaryOfType) {
        newErrors.isPrimary = `A primary ${contactType} already exists. Another one cannot be assigned.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid for button state
  const isFormValid = () => {
    if (!formData.value || formData.value.trim() === '') {
      return false;
    }

    // For email type, validate format
    if (formData.type === 'EMAIL') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.value)) {
        return false;
      }
    }

    // For phone type, check if there's at least 1 digit
    if (formData.type === 'PHONE') {
      const digits = formData.value.replace(/\D/g, '');
      if (digits.length < 1) {
        return false;
      }
    }

    // Check if trying to set as primary when there's already a primary of this type
    if (formData.isPrimary) {
      const contactType = formData.type === 'PHONE' ? 'Phone' : 'Email';
      const hasPrimaryOfType = existingContacts.some(
        c => c.type === contactType && c.primary === 'Yes' && (!contactToEdit || c.id !== contactToEdit.id)
      );
      
      if (hasPrimaryOfType) {
        return false;
      }
    }

    return true;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
      setPrimaryWarning('');
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>
        <h5 className="mb-0">
          <i className="ni ni-mobile-button mr-2" />
          {contactToEdit ? 'Edit Communication Method' : 'Add Communication Method'}
        </h5>
      </ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label htmlFor="contactType">Contact Type</Label>
            <Input
              id="contactType"
              type="select"
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
            >
              <option value="PHONE">Phone</option>
              <option value="EMAIL">Email</option>
            </Input>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="contactValue">
              {formData.type === 'PHONE' ? 'Phone Number' : 'Email Address'}
            </Label>
            <Input
              id="contactValue"
              type={formData.type === 'EMAIL' ? 'email' : 'text'}
              value={formData.value}
              onChange={(e) => handleInputChange('value', e.target.value)}
              placeholder={formData.type === 'PHONE' ? '1234567890123' : 'example@email.com'}
              className={errors.value ? 'is-invalid' : ''}
            />
            {errors.value && (
              <div className="invalid-feedback d-block">
                {errors.value}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <div className="form-check">
              <Input
                id="isPrimary"
                type="checkbox"
                checked={formData.isPrimary}
                onChange={(e) => handleInputChange('isPrimary', e.target.checked)}
                className={errors.isPrimary ? 'is-invalid' : ''}
              />
              <Label htmlFor="isPrimary" className="form-check-label">
                Set as primary contact
              </Label>
            </div>
            {primaryWarning && (
              <Alert color="warning" className="mt-2 mb-0">
                <small>{primaryWarning}</small>
              </Alert>
            )}
            {errors.isPrimary && (
              <div className="invalid-feedback d-block">
                {errors.isPrimary}
              </div>
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          <i className="ni ni-fat-remove mr-2" />
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave} disabled={!isFormValid()}>
          <i className="ni ni-check-bold mr-2" />
          {contactToEdit ? 'Update' : 'Add'} Communication Method
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ContactModal;
